/**
 * Emulation Banner Component
 * 
 * Displays a persistent banner when a user is emulating another user or role.
 * Shows who/what is being emulated and provides a button to stop emulation.
 */

import React from 'react';
import { Box, Typography, Button, Avatar, Chip, Stack } from '@mui/material';
import {
    Person as PersonIcon,
    ExitToApp as ExitIcon,
    Visibility as ViewIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const EmulationBanner = () => {
    const { isEmulating, emulatedUser, actualUser, stopEmulation } = useAuth();

    if (!isEmulating || !emulatedUser) {
        return null;
    }

    // Check if this is a role emulation vs user emulation
    const isRoleEmulation = emulatedUser.isRoleEmulation === true;
    // Check if we're emulating a specific user (vs just a role)
    // Also detect via email - if email doesn't end in @emulation.local, it's a real user
    const hasRealEmail = emulatedUser.email && !emulatedUser.email.endsWith('@emulation.local');
    const isUserEmulation = emulatedUser.isUserEmulation === true || hasRealEmail;
    // Get customer name if available
    const customerName = emulatedUser.customerName;
    // Get emulated user's email for display
    const emulatedEmail = emulatedUser.emulatedUserEmail || emulatedUser.email;

    return (
        <Box
            sx={{
                width: '100%',
                height: 40,
                minHeight: 40,
                flexShrink: 0,
                boxSizing: 'border-box',
                bgcolor: isUserEmulation ? '#7c3aed' : (isRoleEmulation ? '#0ea5e9' : '#7c3aed'), // Purple for user emulation, Blue for role-only
                color: 'white',
                py: 0,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                zIndex: 9999
            }}
        >
            {isUserEmulation ? (
                <PersonIcon sx={{ fontSize: 20 }} />
            ) : isRoleEmulation ? (
                <SecurityIcon sx={{ fontSize: 20 }} />
            ) : (
                <ViewIcon sx={{ fontSize: 20 }} />
            )}

            <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                    {isUserEmulation ? 'Emulating User:' : (isRoleEmulation ? 'Emulating Role:' : 'Viewing as:')}
                </Typography>
                <Avatar
                    sx={{
                        width: 24,
                        height: 24,
                        fontSize: '11px',
                        bgcolor: 'rgba(255,255,255,0.2)'
                    }}
                >
                    {isUserEmulation ? (
                        emulatedUser.firstName?.[0] || emulatedUser.email?.[0] || 'U'
                    ) : isRoleEmulation ? (
                        <SecurityIcon sx={{ fontSize: 14 }} />
                    ) : (
                        emulatedUser.firstName?.[0] || emulatedUser.email?.[0] || 'U'
                    )}
                </Avatar>
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                    {isUserEmulation
                        ? (emulatedUser.displayName || emulatedUser.email)
                        : isRoleEmulation
                            ? `${emulatedUser.role?.charAt(0).toUpperCase()}${emulatedUser.role?.slice(1) || 'Role'}`
                            : (emulatedUser.displayName || emulatedUser.email)
                    }
                </Typography>
                {/* Show emulated user's email if it's a user emulation */}
                {isUserEmulation && emulatedEmail && !emulatedEmail.endsWith('@emulation.local') && (
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>
                        ({emulatedEmail})
                    </Typography>
                )}
                <Chip
                    label={emulatedUser.role || 'user'}
                    size="small"
                    sx={{
                        height: 20,
                        fontSize: '10px',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        textTransform: 'capitalize'
                    }}
                />
                {emulatedUser.emulatedCompanyName && (
                    <>
                        <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                            @
                        </Typography>
                        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                            {emulatedUser.emulatedCompanyName}
                        </Typography>
                    </>
                )}
                {customerName && (
                    <>
                        <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                            →
                        </Typography>
                        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                            {customerName}
                        </Typography>
                    </>
                )}
            </Stack>

            <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', mx: 2 }}>
                |
            </Typography>

            <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                Admin: {actualUser?.email || 'Unknown'}
            </Typography>

            <Button
                variant="outlined"
                size="small"
                startIcon={<ExitIcon sx={{ fontSize: 16, color: 'white' }} />}
                onClick={stopEmulation}
                sx={{
                    ml: 2,
                    bgcolor: 'transparent',
                    color: 'white !important',
                    borderColor: 'white',
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    px: 2,
                    '& .MuiButton-startIcon': {
                        color: 'white'
                    },
                    '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.15)',
                        borderColor: 'white'
                    }
                }}
            >
                EXIT EMULATION
            </Button>
        </Box>
    );
};

export default EmulationBanner;

